import React, { useEffect, useState } from "react";
import { MenuOption } from "../../types/menuOption";
import { Module } from "../../types/module";
import IconPicker from "../IconPicker";

interface MenuOptionModalProps {
    menuOption: MenuOption;
    onClose: () => void;
    allMenuOptions: MenuOption[];
    modules: Module[];
    onSave: (menuOption: MenuOption) => void;
}

const MenuOptionModal: React.FC<MenuOptionModalProps> = ({ menuOption, allMenuOptions, modules, onClose, onSave }) => {
    const [menuName, setMenuName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [moduleId, setModuleId] = useState<number | "">("");
    const [routePath, setRoutePath] = useState<string>('');
    const [componentName, setComponentName] = useState<string>('');
    const [parentId, setParentId] = useState<number | null>(null);
    const [iconClass, setIconClass] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(true);
    const [isFormValid, setIsformValid] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ menuName?: string; menuDescription?: string }>({});
    
    const handleSave = () => {
        onSave({ ...menuOption, menu_name: menuName, module_id: Number(moduleId), description, route_path: routePath, parent_id: parentId, component_name: componentName, icon_class: iconClass, is_active: isActive });
    };

    useEffect(() => {
        if (menuOption.menu_id) {
            setMenuName(menuOption.menu_name);
            setModuleId(menuOption.module_id || "");
            setDescription(menuOption.description || '');
            setParentId(menuOption.parent_id || null);
            setRoutePath(menuOption.route_path || '');
            setComponentName(menuOption.component_name || '');
            setIconClass(menuOption.icon_class || '');
            setIsActive(menuOption.is_active);
        }
        else {
            // Nuevo
            setMenuName("");
            setModuleId("");
            setParentId(null);
            setRoutePath("");
            setComponentName("");
            setDescription("");
            setIsActive(true);
        }
        setErrors({});
    }, [menuOption]);

    useEffect(() => {
        const validarErrores =
            menuName.trim() !== '' &&
            (!errors.menuName || errors.menuName === '') &&
            (!errors.menuDescription || errors.menuDescription === '');
        setIsformValid(validarErrores);
    }, [menuName, isActive, errors]);

    // Filtrar opciones que puedan ser padres:
    //   1) No ser la misma que estamos editando (evitar loops)
    //   2) Podrías filtrar también por el mismo module_id, si así lo decides
    const possibleParents = allMenuOptions.filter((mo) => {
        if ((menuOption && mo.menu_id === menuOption.menu_id) || !mo.is_active) return false;

        return true;
    });

    return (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content shadow-lg bg-light">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {menuOption.menu_id ? 'Editar' : 'Crear'} opción de menú ({menuOption.menu_id})
                        </h5>
                        <button type="button" className="btn-close" data-dismiss='modal' aria-label='Close' onClick={onClose}>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <label htmlFor="menuName">Nombre:</label>
                            <input
                                type="text"
                                className={`form-control ${errors.menuName ? 'is-invalid' : ''}`}
                                id="menuName"
                                value={menuName}
                                onChange={(e) => setMenuName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="description">Descripción:</label>
                            <textarea
                                className={`form-control ${errors.menuDescription ? 'is-invalid' : ''}`}
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                        <div className="mb-3 form-group">
                            <label htmlFor="moduleIdSelect" className="form-label">Módulo</label>
                            <select id="moduleIdSelect"
                                className="form-select"
                                value={moduleId}
                                onChange={(e) => setModuleId(e.target.value ? Number(e.target.value) : '')}
                            >
                                <option value="">Seleccione un módulo</option>
                                {modules.map((module) => {
                                    if (!module.is_active) return null;
                                    return (<option key={module.module_id} value={module.module_id}>
                                        {module.module_name}
                                    </option>)
                                })}
                            </select>
                        </div>
                        <div className="mb-3 form-group">
                            <label htmlFor="parenteIdSelect" className="form-label">Opción Padre</label>
                            <select
                                name=""
                                id="parentIdSelect"
                                className="form-select"
                                value={parentId || ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setParentId(val === "" ? null : Number(val));
                                }}
                            >
                                <option value="">(Ninguno - nivel 1)</option>
                                {possibleParents.map((mo) => (
                                    <option key={mo.menu_id} value={mo.menu_id}>
                                        {mo.menu_name}
                                    </option>
                                ))}
                            </select>
                            <small className="text-muted">
                                Seleccione una opción padre para crear un submenú (nivel 2 o 3)
                            </small>
                        </div>
                        <div className="mb-3 form-group">
                            <label htmlFor="routePath">Ruta:</label>
                            <input
                                type="text"
                                className="form-control"
                                id="routePath"
                                value={routePath}
                                onChange={(e) => setRoutePath(e.target.value)}
                            />
                        </div>
                        <div className="mb-3 form-group">
                            <label htmlFor="componentName">Componente:</label>
                            <input
                                type="text"
                                className="form-control"
                                id="componentName"
                                value={componentName}
                                onChange={(e) => setComponentName(e.target.value)}
                            />
                        </div>
                        <div className="mb-3 form-group">
                            <label htmlFor="iconClassPicker" className="form-label">Ícono</label>
                            <IconPicker
                                value={iconClass}
                                onChange={(newIcon) => setIconClass(newIcon)}
                            />
                        </div>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="isActive"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="isActive">Activo</label>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={!isFormValid}
                        >
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MenuOptionModal;

